import { useCallback, useEffect, useState } from 'react'
import { evaluationsService } from 'api/courses/evaluations'
import { IEvaluationResume } from 'models/evaluation.model'
import { EvaluationResponseStatus } from 'enum/evaluationResponseStatus.enum'
import { createInitialEvaluationResume } from 'helpers/evaluations/initialEvaluationResume'
import { TableSkeleton } from 'components/reusables/skeletons'
import { useCourse } from 'contexts'
import WithoutStarting from './withoutStarting'
import Started from './started'
import InProgress from './Resume'
import { Wrapper, SkeletonWrapper } from './styled'
import PreviewMode from './PreviewMode'

interface IProps {
  evaluationId: number
  onShowEvaluation: (evaluation: IEvaluationResume) => void
}

const Detail = ({ evaluationId, onShowEvaluation }: IProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [evaluation, setEvaluation] = useState<IEvaluationResume>(
    createInitialEvaluationResume
  )

  const { previewMode } = useCourse()

  const fetchEvaluation = useCallback(async () => {
    setIsLoading(true)
    try {
      return evaluationsService.getEvaluation(evaluationId)
    } finally {
      setIsLoading(false)
    }
  }, [evaluationId])

  useEffect(() => {
    fetchEvaluation().then((response) => {
      setEvaluation(response)
    })
  }, [fetchEvaluation])

  const showEvaluation = useCallback(async () => {
    const response = await fetchEvaluation()
    onShowEvaluation(response)
  }, [fetchEvaluation, onShowEvaluation])

  const { status } = evaluation

  const finished =
    status === EvaluationResponseStatus.abandoned ||
    status === EvaluationResponseStatus.completed

  if (isLoading)
    return (
      <SkeletonWrapper>
        <TableSkeleton />
      </SkeletonWrapper>
    )

  if (previewMode)
    return (
      <PreviewMode evaluation={evaluation} showEvaluation={showEvaluation} />
    )

  return (
    <Wrapper>
      {status === EvaluationResponseStatus.notStarted && (
        <WithoutStarting
          evaluation={evaluation}
          showEvaluation={showEvaluation}
        />
      )}
      {finished && (
        <Started evaluation={evaluation} showEvaluation={showEvaluation} />
      )}
      {status === EvaluationResponseStatus.inProgress && (
        <InProgress evaluation={evaluation} showEvaluation={showEvaluation} />
      )}
    </Wrapper>
  )
}

export default Detail
